import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as THREE from 'three';
import { cn } from '../../utils/cn';

const ThreeJSOrb = ({ isConnected = false, isSpeaking = false, audioLevel = 0 }) => {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const orbRef = useRef(null);
  const lensFlareRef = useRef(null);
  const animationRef = useRef(null);
  const [orbScale, setOrbScale] = useState(1);
  const [glowLevel, setGlowLevel] = useState(0);
  const [pulse, setPulse] = useState(0);

  useEffect(() => {
    if (!mountRef.current) return;

    const currentMount = mountRef.current;

    // Scene setup
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    
    renderer.setSize(300, 300);
    renderer.setClearColor(0x000000, 0);
    currentMount.appendChild(renderer.domElement);

    // Create the main orb geometry
    const geometry = new THREE.SphereGeometry(1.2, 64, 64);
    
    // Create shader material for the orb
    const material = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        isConnected: { value: isConnected ? 1.0 : 0.0 },
        isSpeaking: { value: isSpeaking ? 1.0 : 0.0 },
        resolution: { value: new THREE.Vector2(300, 300) }
      },
      vertexShader: `
        varying vec2 vUv;
        varying vec3 vPosition;
        uniform float time;
        uniform float isSpeaking;
        
        void main() {
          vUv = uv;
          vPosition = position;
          
          vec3 pos = position;
          
          // Add speaking animation
          if (isSpeaking > 0.5) {
            float wave = sin(time * 8.0 + position.x * 5.0) * 0.05;
            pos += normal * wave;
          }
          
          gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
        }
      `,
      fragmentShader: `
        uniform float time;
        uniform float isConnected;
        uniform float isSpeaking;
        uniform vec2 resolution;
        varying vec2 vUv;
        varying vec3 vPosition;
        
        // Blue color palette
        vec3 deepBlue = vec3(0.1, 0.3, 0.8);
        vec3 lightBlue = vec3(0.4, 0.7, 1.0);
        vec3 whiteBlue = vec3(0.8, 0.9, 1.0);
        vec3 speakingColor = vec3(0.2, 0.6, 1.0);
        
        // Noise function for enhanced shimmer
        float noise(vec2 st) {
          return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
        }
        
        void main() {
          vec2 center = vec2(0.5, 0.5);
          float dist = distance(vUv, center);
          
          // Create multiple ripple effects
          float ripple1 = sin(dist * 15.0 - time * 6.0) * 0.1;
          float ripple2 = sin(dist * 25.0 - time * 4.0) * 0.05;
          float ripple3 = sin(dist * 35.0 - time * 8.0) * 0.03;
          float ripples = ripple1 + ripple2 + ripple3;
          
          // Enhanced shimmer with multiple layers
          float shimmer1 = sin(vUv.x * 15.0 + time * 4.0) * sin(vUv.y * 12.0 + time * 3.0) * 0.15;
          float shimmer2 = sin(vUv.x * 8.0 - time * 2.0) * sin(vUv.y * 6.0 - time * 2.5) * 0.1;
          float shimmer3 = noise(vUv * 20.0 + time * 0.5) * 0.08;
          float totalShimmer = shimmer1 + shimmer2 + shimmer3;
          
          // Animated color waves
          float colorWave1 = sin(time * 2.0 + dist * 8.0) * 0.5 + 0.5;
          float colorWave2 = sin(time * 1.5 - dist * 12.0) * 0.5 + 0.5;
          
          // Base color mixing with blue palette
          vec3 baseColor = mix(deepBlue, lightBlue, isConnected);
          baseColor = mix(baseColor, whiteBlue, colorWave1 * 0.3);
          
          // Speaking state with blue tones
          if (isSpeaking > 0.5) {
            float speakingIntensity = sin(time * 8.0) * 0.5 + 0.5;
            baseColor = mix(baseColor, speakingColor, speakingIntensity * 0.6);
          }
          
          // Create enhanced glow effect
          float glow = 1.0 - smoothstep(0.0, 0.9, dist);
          float innerGlow = 1.0 - smoothstep(0.0, 0.5, dist);
          
          // Combine all effects
          vec3 finalColor = baseColor;
          finalColor += glow * whiteBlue * 0.4;
          finalColor += innerGlow * lightBlue * 0.2;
          finalColor += totalShimmer * whiteBlue;
          finalColor += colorWave2 * lightBlue * 0.15;
          
          float alpha = glow * (0.7 + isConnected * 0.3) + ripples + totalShimmer * 0.5;
          alpha = clamp(alpha, 0.0, 1.0);
          
          gl_FragColor = vec4(finalColor, alpha);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending
    });

    const orb = new THREE.Mesh(geometry, material);
    scene.add(orb);

    // Add particle system
    const particleGeometry = new THREE.BufferGeometry();
    const particleCount = 100;
    const positions = new Float32Array(particleCount * 3);
    
    for (let i = 0; i < particleCount * 3; i += 3) {
      positions[i] = (Math.random() - 0.5) * 10;
      positions[i + 1] = (Math.random() - 0.5) * 10;
      positions[i + 2] = (Math.random() - 0.5) * 10;
    }
    
    particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    
    const particleMaterial = new THREE.PointsMaterial({
      color: 0x4A90E2, // Blue color for particles
      size: 0.05,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending
    });
    
    const particles = new THREE.Points(particleGeometry, particleMaterial);
    scene.add(particles);

    // Create lens flare effect for speaking state
    const lensFlareGeometry = new THREE.RingGeometry(1.5, 2.5, 32);
    const lensFlareMaterial = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        isSpeaking: { value: isSpeaking ? 1.0 : 0.0 },
        opacity: { value: 0.0 }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float time;
        uniform float isSpeaking;
        uniform float opacity;
        varying vec2 vUv;
        
        void main() {
          vec2 center = vec2(0.5, 0.5);
          float dist = distance(vUv, center);
          
          // Create lens flare rings with blue tones
          float ring1 = sin((dist - time * 0.5) * 15.0) * 0.5 + 0.5;
          float ring2 = sin((dist - time * 0.3) * 10.0) * 0.3 + 0.3;
          float ring3 = sin((dist - time * 0.7) * 20.0) * 0.2 + 0.2;
          
          float flare = (ring1 + ring2 + ring3) * (1.0 - smoothstep(0.2, 0.8, dist));
          
          // Blue lens flare color
          vec3 flareColor = vec3(0.3, 0.7, 1.0); // Cool blue
          
          gl_FragColor = vec4(flareColor, flare * opacity * isSpeaking);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide
    });

    const lensFlare = new THREE.Mesh(lensFlareGeometry, lensFlareMaterial);
    scene.add(lensFlare);

    camera.position.z = 3;

    // Store references
    sceneRef.current = scene;
    rendererRef.current = renderer;
    orbRef.current = orb;
    lensFlareRef.current = lensFlare;

    // Animation loop
    const animate = () => {
      const time = Date.now() * 0.001;
      
      if (orbRef.current) {
        orbRef.current.material.uniforms.time.value = time;
        orbRef.current.rotation.y = time * 0.2;
        orbRef.current.rotation.x = Math.sin(time * 0.3) * 0.1;
        orbRef.current.scale.set(orbScale, orbScale, orbScale); // Animate orb scale
      }
      
      // Animate lens flare
      if (lensFlareRef.current) {
        lensFlareRef.current.material.uniforms.time.value = time;
        lensFlareRef.current.rotation.z = time * 0.1;
        
        // Animate opacity based on speaking state
        const targetOpacity = isSpeaking ? 0.8 : 0.0;
        const currentOpacity = lensFlareRef.current.material.uniforms.opacity.value;
        lensFlareRef.current.material.uniforms.opacity.value = THREE.MathUtils.lerp(currentOpacity, targetOpacity, 0.05);
      }
      
      // Animate particles
      particles.rotation.y = time * 0.1;
      particles.rotation.x = time * 0.05;
      
      renderer.render(scene, camera);
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();

    // Cleanup function
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (currentMount && renderer.domElement) {
        currentMount.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update uniforms when props change
  useEffect(() => {
    if (orbRef.current) {
      orbRef.current.material.uniforms.isConnected.value = isConnected ? 1.0 : 0.0;
      orbRef.current.material.uniforms.isSpeaking.value = isSpeaking ? 1.0 : 0.0;
    }
    if (lensFlareRef.current) {
      lensFlareRef.current.material.uniforms.isSpeaking.value = isSpeaking ? 1.0 : 0.0;
    }
  }, [isConnected, isSpeaking]);

  useEffect(() => {
    let scaleAnim;
    let t = 0;
    function animateScale() {
      t += 0.04;
      if (isSpeaking) {
        setOrbScale(1.18 + (audioLevel || 0) * 0.18); // Grow with audio
        setGlowLevel(0.5 + (audioLevel || 0) * 0.5); // Glow with audio
      } else {
        setOrbScale(1 + Math.sin(t) * 0.06);
        setGlowLevel(0.25 + Math.abs(Math.sin(t)) * 0.15);
      }
      scaleAnim = requestAnimationFrame(animateScale);
    }
    animateScale();
    return () => cancelAnimationFrame(scaleAnim);
  }, [isSpeaking, audioLevel]);

  return (
    <div className="relative w-full h-full">
      {/* Three.js Canvas */}
      <div 
        ref={mountRef} 
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{ width: '100%', height: '100%' }}
      />
      {/* Sharp edge animated rings (instead of glow) */}
      <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
        {[0, 1, 2].map((i) => {
          // Each ring has a different phase offset for a ripple effect
          const base = isSpeaking ? (audioLevel || 0.1) : pulse;
          const delay = i * 0.33;
          // Animate scale and opacity outward
          const progress = ((base + delay) % 1);
          const scale = 1.1 + progress * 0.7 + i * 0.1;
          const opacity = 0.18 * (1 - progress);
          return (
            <div
              key={i}
              className="absolute left-1/2 top-1/2 border-2 border-blue-400 rounded-full pointer-events-none"
              style={{
                width: '70%',
                height: '70%',
                transform: `translate(-50%, -50%) scale(${scale})`,
                opacity,
                transition: 'opacity 0.2s',
                boxSizing: 'border-box',
                borderColor: i === 0 ? '#6ec6ff' : i === 1 ? '#ffb347' : '#a7ffeb',
              }}
            />
          );
        })}
      </div>
      {/* Microphone/Stop Icon Overlay with Liquid Glass Effect */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
        <div className={cn(
          "relative transition-all duration-500 flex items-center justify-center",
          "w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28", // Larger container
          "rounded-full backdrop-blur-xl bg-white/20 border border-white/30",
          "shadow-2xl shadow-blue-500/20",
          // Liquid glass effect with animations
          "before:absolute before:inset-0 before:rounded-full",
          "before:bg-gradient-to-br before:from-white/40 before:via-blue-200/30 before:to-transparent",
          "before:animate-liquid-pulse",
          // State-based styling with blue theme
          isConnected
            ? isSpeaking
              ? "scale-110 bg-blue-500/20 border-blue-300/40 shadow-blue-500/30 animate-pulsing-glow"
              : "scale-105 bg-blue-500/20 border-blue-300/40 shadow-blue-500/30"
            : "hover:scale-105 hover:bg-blue-400/20"
        )}>
          {/* Shimmer effect overlay */}
          <div className="absolute inset-0 rounded-full overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
          </div>
          {/* Icon */}
          <div className="relative z-10">
            {isConnected ? (
              // Stop icon when connected (speaking or listening)
              <svg 
                className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 drop-shadow-lg" 
                fill="#fff" 
                viewBox="0 0 24 24"
              >
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
            ) : (
              // Microphone icon when disconnected
              <svg 
                className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 drop-shadow-lg" 
                fill="#fff" 
                viewBox="0 0 24 24"
              >
                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
              </svg>
            )}
          </div>
          {/* Breathing glow effect with blue theme */}
          <div className={cn(
            "absolute inset-0 rounded-full transition-all duration-300",
            isConnected && "animate-breathing",
            isSpeaking 
              ? "bg-gradient-to-br from-blue-400/30 to-blue-600/20" 
              : isConnected
              ? "bg-gradient-to-br from-blue-400/30 to-blue-600/20"
              : "bg-gradient-to-br from-blue-300/20 to-blue-500/10"
          )} />
        </div>
      </div>
      
      {/* Status Text */}
      {isConnected && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 pointer-events-none z-10">
          <p className={cn(
            "text-xs sm:text-sm font-medium text-center px-3 py-1 rounded-full backdrop-blur-sm border",
            isSpeaking 
              ? "text-green-700 bg-green-50/80 border-green-200" 
              : "text-orange-700 bg-orange-50/80 border-orange-200"
          )}>
            {isSpeaking ? "AI Speaking..." : "Listening..."}
          </p>
        </div>
      )}
      
      {/* Instruction Text for Idle State */}
      {!isConnected && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 pointer-events-none z-10">
          <p className="text-xs sm:text-sm font-medium text-orange-600 text-center px-3 py-1 rounded-full bg-white/80 backdrop-blur-sm border border-orange-200">
            Tap to start conversation
          </p>
        </div>
      )}
    </div>
  );
};

ThreeJSOrb.propTypes = {
  isConnected: PropTypes.bool,
  isSpeaking: PropTypes.bool,
  audioLevel: PropTypes.number,
};

export default ThreeJSOrb;
